// Utility to parse your specific DynamoDB response format

export const parseYourGrowthSessionResponse = (responseData) => {
  if (!responseData || !responseData.growthSessions) {
    return [];
  }

  return responseData.growthSessions.map(session => {
    try {
      // Extract session_data and parse if it's a JSON string
      let sessionData = {};
      if (session.session_data && session.session_data.S) {
        try {
          sessionData = JSON.parse(session.session_data.S);
        } catch (e) {
          console.error('Error parsing session_data:', e);
          sessionData = {};
        }
      }

      // Extract pip_data and parse if it's a JSON string
      let pipData = null;
      if (session.pip_data && session.pip_data.S) {
        try {
          pipData = JSON.parse(session.pip_data.S);
        } catch (e) {
          console.error('Error parsing pip_data:', e);
          pipData = null;
        }
      }

      // Create the parsed session object
      const parsedSession = {
        id: session.id?.S || '',
        user_id: session.user_id?.S || '',
        lead_id: session.lead_id?.S || '',
        manager_id: session.manager_id?.S || '',
        status: session.status?.S || '',
        date: session.date?.S || '',
        review_period_from: session.review_period_from?.S || '',
        review_period_to: session.review_period_to?.S || '',
        created_at: session.created_at?.S || '',
        updated_at: session.updated_at?.S || '',
        created_by: session.created_by?.S || '',
        manager_comments: session.manager_comments?.S || '',
        session_data: sessionData,
        pip_data: pipData
      };

      return parsedSession;
    } catch (error) {
      console.error('Error parsing individual session:', error, session);
      return null;
    }
  }).filter(session => session !== null);
};

// Function to automatically assign leads to users without them
export const assignLeadsToUsers = async (sessions, users, updateGrowthSessionAPI) => {
  const availableLeads = users.filter(user => user.role === 'Lead');
  
  if (availableLeads.length === 0) {
    console.warn('No leads available for assignment');
    return sessions;
  }

  // Find sessions that need lead assignment
  const sessionsNeedingLeads = sessions.filter(session => 
    session.status === 'lead_assigned' && 
    (!session.lead_id || String(session.lead_id || '').trim() === '')
  );

  if (sessionsNeedingLeads.length === 0) {
    console.log('All user assignments already have leads assigned');
    return sessions;
  }

  console.log(`Found ${sessionsNeedingLeads.length} user assignments that need lead assignment`);

  // Assign leads in round-robin fashion
  const updatedSessions = [...sessions];
  const leadAssignmentPromises = [];

  for (let i = 0; i < sessionsNeedingLeads.length; i++) {
    const session = sessionsNeedingLeads[i];
    const assignedLead = availableLeads[i % availableLeads.length];
    
    console.log(`Assigning lead ${assignedLead.name} (${assignedLead.email}) to user ${session.user_id}`);

    // Update the session in our local array
    const sessionIndex = updatedSessions.findIndex(s => s.id === session.id);
    if (sessionIndex !== -1) {
      updatedSessions[sessionIndex] = {
        ...updatedSessions[sessionIndex],
        lead_id: assignedLead.email,
        updated_at: new Date().toISOString(),
        auto_assigned_lead: true
      };
    }

    // Queue the API update
    const updatePromise = updateGrowthSessionAPI({
      id: session.id,
      lead_id: assignedLead.email,
      updated_at: new Date().toISOString()
    });

    leadAssignmentPromises.push(updatePromise);
  }

  // Execute all updates
  try {
    await Promise.all(leadAssignmentPromises);
    console.log(`Successfully assigned leads to ${sessionsNeedingLeads.length} users`);
    return updatedSessions;
  } catch (error) {
    console.error('Error updating sessions with lead assignments:', error);
    // Return the updated sessions even if API updates failed
    return updatedSessions;
  }
};

// Helper to get session statistics
export const getSessionStatistics = (sessions, users) => {
  const usersByRole = {
    User: users.filter(u => u.role === 'User').length,
    Lead: users.filter(u => u.role === 'Lead').length,
    Manager: users.filter(u => u.role === 'Manager').length
  };

  const sessionsByStatus = {
    'lead_assigned': sessions.filter(s => s.status === 'lead_assigned').length,
    'completed': sessions.filter(s => s.status === 'completed').length,
    'in_progress': sessions.filter(s => s.status === 'in_progress').length,
    'scheduled': sessions.filter(s => s.status === 'scheduled').length,
    'draft': sessions.filter(s => s.status === 'draft').length
  };

  const usersWithoutLeads = sessions.filter(s => 
    s.status === 'lead_assigned' && (!s.lead_id || String(s.lead_id || '').trim() === '')
  ).length;

  return {
    users: usersByRole,
    sessions: sessionsByStatus,
    usersWithoutLeads
  };
};

// Helper to validate session data
export const validateSession = (session) => {
  const errors = [];

  if (!session.user_id) errors.push('User ID is required');
  if (!session.manager_id) errors.push('Manager ID is required');
  if (session.status === 'lead_assigned' && !session.lead_id) {
    errors.push('Lead assignment is required for user assignment status');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Helper to format session data for display
export const formatSessionData = (session, users) => {
  const user = users.find(u => u.email === session.user_id);
  const lead = users.find(u => u.email === session.lead_id);
  const manager = users.find(u => u.email === session.manager_id);

  return {
    ...session,
    userDisplayName: user ? `${user.name} (${user.email})` : session.user_id,
    leadDisplayName: lead ? `${lead.name} (${lead.email})` : session.lead_id || 'Unassigned',
    managerDisplayName: manager ? `${manager.name} (${manager.email})` : session.manager_id,
    teamInfo: session.session_data?.dynamic_fields ? 
      `${session.session_data.dynamic_fields.team_name || 'No Team'} - ${session.session_data.dynamic_fields.department || 'No Dept'}` :
      'No Team Info',
    statusDisplay: session.status.replace('_', ' ').toUpperCase(),
    dateDisplay: session.date ? new Date(session.date).toLocaleDateString() : 'Not Set'
  };
};